import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Laptop, HardDrive, CheckCircle, XCircle, Info, Monitor } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DownloadsPage = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Fehler beim Laden des Abonnements');
    } finally {
      setLoading(false);
    }
  };

  const hasFeature = (featureKey) => {
    if (!subscription?.plan?.features) return false;
    return subscription.plan.features[featureKey] === true;
  };

  const handleDownload = async (downloadType) => {
    let platform = '';
    
    if (downloadType === 'obd-agent-windows') {
      platform = 'windows';
    } else if (downloadType === 'obd-agent-mac') {
      platform = 'macos';
    } else if (downloadType === 'obd-agent-linux') {
      platform = 'linux';
    } else if (downloadType === 'obd-agent-android') {
      platform = 'android';
    } else if (downloadType === 'android-studio-project') {
      platform = 'android-project';
    }
    
    if (platform) {
      toast.info('Download wird vorbereitet...');
      
      try {
        // First check if file is available
        const checkUrl = `${API_URL}/api/obd/agent/download/${platform}`;
        const response = await axios.get(checkUrl, {
          responseType: 'blob',
          validateStatus: (status) => status < 500 // Don't throw on 404
        });
        
        if (response.status === 404) {
          // File not available yet
          const errorData = await response.data.text();
          const errorJson = JSON.parse(errorData);
          toast.error(errorJson.detail || `${platform} Version ist noch nicht verfügbar. Bitte versuchen Sie es später erneut.`);
          return;
        }
        
        if (response.status === 200) {
          // File available, trigger download
          const downloadUrl = `${API_URL}/api/obd/agent/download/${platform}`;
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `carcortex-obd-agent-${platform}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          toast.success('Download gestartet!');
        }
      } catch (error) {
        console.error('Download error:', error);
        if (error.response?.status === 404) {
          toast.error(`${platform === 'windows' ? 'Windows' : platform === 'macos' ? 'macOS' : 'Linux'} Version ist noch nicht verfügbar. Bitte versuchen Sie die Linux-Version oder warten Sie auf die Veröffentlichung.`);
        } else {
          toast.error('Fehler beim Download. Bitte versuchen Sie es später erneut.');
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="p-8" style={{ backgroundColor: '#0B0F14', minHeight: '100vh' }}>
        <div className="flex items-center justify-center h-64">
          <p style={{ color: '#94A3B8' }}>Lädt...</p>
        </div>
      </div>
    );
  }

  const hasOBDFeature = hasFeature('obd_live_diagnosis');

  return (
    <div className="p-8" style={{ backgroundColor: '#0B0F14', minHeight: '100vh' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: '#E6EDF3' }}>
            <Download className="w-8 h-8" style={{ color: '#7CD4FD' }} />
            Downloads
          </h1>
          <p className="mt-2" style={{ color: '#94A3B8' }}>
            Laden Sie Software und Tools herunter, die in Ihrem Abonnement enthalten sind
          </p>
        </div>

        {/* Current Plan Info */}
        <Card className="mb-6" style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#94A3B8' }}>Ihr aktueller Plan:</p>
                <p className="text-xl font-bold" style={{ color: '#7CD4FD' }}>
                  {subscription?.plan?.name || 'Kein Abonnement'}
                </p>
              </div>
              {subscription?.has_subscription && (
                <span 
                  className="px-4 py-2 rounded-full text-sm font-bold"
                  style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}
                >
                  ✓ Aktiv
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* OBD Desktop Agent */}
        {hasOBDFeature ? (
          <Card style={{ backgroundColor: '#1A1F2E', borderColor: '#7CD4FD' }}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: '#7CD4FD20' }}
                  >
                    <Laptop className="w-6 h-6" style={{ color: '#7CD4FD' }} />
                  </div>
                  <div>
                    <CardTitle style={{ color: '#E6EDF3' }}>
                      CarCortex OBD Desktop Agent
                    </CardTitle>
                    <CardDescription style={{ color: '#94A3B8' }}>
                      Live-Diagnose Software für Windows, macOS und Linux
                    </CardDescription>
                  </div>
                </div>
                <span 
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}
                >
                  ✓ Verfügbar
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Features */}
                <div>
                  <h4 className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>Features:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2" style={{ color: '#94A3B8' }}>
                      <CheckCircle className="w-4 h-4" style={{ color: '#22C55E' }} />
                      Live-Verbindung zum Fahrzeug über OBD-Dongle
                    </li>
                    <li className="flex items-center gap-2" style={{ color: '#94A3B8' }}>
                      <CheckCircle className="w-4 h-4" style={{ color: '#22C55E' }} />
                      Echtzeit-Datenübertragung zur CarCortex-AI Cloud
                    </li>
                    <li className="flex items-center gap-2" style={{ color: '#94A3B8' }}>
                      <CheckCircle className="w-4 h-4" style={{ color: '#22C55E' }} />
                      Automatische Diagnose während der Fahrt
                    </li>
                    <li className="flex items-center gap-2" style={{ color: '#94A3B8' }}>
                      <CheckCircle className="w-4 h-4" style={{ color: '#22C55E' }} />
                      Unterstützt alle gängigen OBD-II Protokolle
                    </li>
                  </ul>
                </div>

                {/* System Requirements */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#2A3142' }}>
                  <h4 className="font-semibold mb-2 flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                    <Info className="w-4 h-4" style={{ color: '#7CD4FD' }} />
                    Systemanforderungen:
                  </h4>
                  <div className="grid md:grid-cols-3 gap-4 text-sm" style={{ color: '#94A3B8' }}>
                    <div>
                      <p className="font-semibold mb-1" style={{ color: '#E6EDF3' }}>Windows</p>
                      <p>Windows 10/11 (64-bit)</p>
                      <p>4 GB RAM</p>
                      <p>USB-Port für Dongle</p>
                    </div>
                    <div>
                      <p className="font-semibold mb-1" style={{ color: '#E6EDF3' }}>macOS</p>
                      <p>macOS 11.0+</p>
                      <p>4 GB RAM</p>
                      <p>USB-Port für Dongle</p>
                    </div>
                    <div>
                      <p className="font-semibold mb-1" style={{ color: '#E6EDF3' }}>Linux</p>
                      <p>Ubuntu 20.04+ / Fedora 34+</p>
                      <p>4 GB RAM</p>
                      <p>USB-Port für Dongle</p>
                    </div>
                  </div>
                </div>

                {/* Download Buttons */}
                <div>
                  <h4 className="font-semibold mb-3" style={{ color: '#E6EDF3' }}>Download für Ihr System:</h4>
                  
                  {/* Windows Info Box */}
                  <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#22C55E10', border: '1px solid #22C55E' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">🎉</span>
                      <p className="font-bold" style={{ color: '#22C55E' }}>NEU: Windows v1.0.1 - ONE-CLICK Installer!</p>
                    </div>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      <strong>25 MB</strong> - Python automatisch enthalten | Desktop-Verknüpfung wird erstellt | Nur eine Datei anklicken!
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-4 gap-4">
                    {/* Windows */}
                    <Button
                      onClick={() => handleDownload('obd-agent-windows')}
                      className="w-full flex items-center justify-center gap-2 py-6"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      <HardDrive className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-bold">Windows ⭐</p>
                        <p className="text-xs opacity-80">v1.0.1 (25 MB)</p>
                      </div>
                    </Button>

                    {/* macOS */}
                    <Button
                      onClick={() => handleDownload('obd-agent-mac')}
                      className="w-full flex items-center justify-center gap-2 py-6"
                      style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                    >
                      <Laptop className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-bold">macOS</p>
                        <p className="text-xs opacity-80">Version 1.0.1</p>
                      </div>
                    </Button>

                    {/* Linux */}
                    <Button
                      onClick={() => handleDownload('obd-agent-linux')}
                      className="w-full flex items-center justify-center gap-2 py-6"
                      style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                    >
                      <Monitor className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-bold">Linux</p>
                        <p className="text-xs opacity-80">Version 1.0.1</p>
                      </div>
                    </Button>

                    {/* Android */}
                    <Button
                      onClick={() => handleDownload('obd-agent-android')}
                      className="w-full flex items-center justify-center gap-2 py-6"
                      style={{ backgroundColor: '#3DDC84', color: '#fff' }}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.6,9.48l1.84-3.18c0.16-0.31,0.04-0.69-0.26-0.85c-0.29-0.15-0.65-0.06-0.83,0.22l-1.88,3.24 c-2.86-1.21-6.08-1.21-8.94,0L5.65,5.67c-0.19-0.29-0.58-0.38-0.87-0.2C4.5,5.65,4.41,6.01,4.56,6.3L6.4,9.48 C3.3,11.25,1.28,14.44,1,18h22C22.72,14.44,20.7,11.25,17.6,9.48z M7,15.25c-0.69,0-1.25-0.56-1.25-1.25 c0-0.69,0.56-1.25,1.25-1.25S8.25,13.31,8.25,14C8.25,14.69,7.69,15.25,7,15.25z M17,15.25c-0.69,0-1.25-0.56-1.25-1.25 c0-0.69,0.56-1.25,1.25-1.25s1.25,0.56,1.25,1.25C18.25,14.69,17.69,15.25,17,15.25z"/>
                      </svg>
                      <div className="text-left">
                        <p className="font-bold">Android</p>
                        <p className="text-xs opacity-80">Version 1.0.1</p>
                      </div>
                    </Button>
                  </div>
                  {/* Android Studio Project Download Info - ADMIN ONLY */}
                  {user?.is_admin === true && (
                    <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}>
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🛠️</div>
                        <div className="flex-1">
                          <h3 className="font-bold text-base mb-2" style={{ color: '#E6EDF3' }}>
                            Android App kompilieren (Admin)
                          </h3>
                          <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
                            Lade das Android Studio Projekt herunter, baue die APK und uploade sie unten.
                          </p>
                          <Button
                            onClick={() => handleDownload('android-studio-project')}
                            className="flex items-center gap-2"
                            style={{ backgroundColor: '#F59E0B', color: '#000' }}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M13 7H7v6h6V7z"/>
                              <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd"/>
                            </svg>
                            Android Studio Projekt (ZIP)
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-sm mt-4 text-center" style={{ color: '#94A3B8' }}>
                    💡 <strong>Tipp:</strong> Der Download wird über GitHub Actions automatisch kompiliert. 
                    Wenn ein Download nicht verfügbar ist, werden Sie benachrichtigt.
                  </p>
                </div>

                {/* Installation Guide */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFA50010', border: '1px solid #FFA50030' }}>
                  <h3 className="font-bold mb-3 text-base" style={{ color: '#FFA500' }}>
                    ⚙️ Windows Installation - Voraussetzungen
                  </h3>
                  <div className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                    <p>
                      <strong style={{ color: '#E6EDF3' }}>1. Python installieren</strong>
                      <br/>
                      Der Agent benötigt Python 3.11 oder neuer. Laden Sie Python von{' '}
                      <a 
                        href="https://www.python.org/downloads/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#1EA0E6', textDecoration: 'underline' }}
                      >
                        python.org
                      </a>{' '}
                      herunter.
                    </p>
                    <p className="pl-4" style={{ color: '#22C55E' }}>
                      ✅ <strong>Wichtig:</strong> Aktivieren Sie bei der Installation:
                      <br/>
                      • "Add Python to PATH"
                      <br/>
                      • "tcl/tk and IDLE"
                    </p>
                    <p>
                      <strong style={{ color: '#E6EDF3' }}>2. Agent installieren</strong>
                      <br/>
                      Nach dem Download:
                      <br/>
                      • ZIP-Datei entpacken
                      <br/>
                      • Doppelklick auf <code style={{ color: '#7CD4FD' }}>install.bat</code>
                      <br/>
                      • Agent starten mit <code style={{ color: '#7CD4FD' }}>start_agent.bat</code>
                    </p>
                    <p>
                      📖 Eine ausführliche Anleitung finden Sie in der mitgelieferten README.txt
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
            <CardContent className="p-12 text-center">
              <XCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#94A3B8', opacity: 0.5 }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#E6EDF3' }}>
                Keine Downloads verfügbar
              </h3>
              <p className="mb-4" style={{ color: '#94A3B8' }}>
                Ihr aktueller Plan enthält keine herunterladbaren Software-Komponenten.
              </p>
              <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>
                Upgraden Sie Ihren Plan, um Zugriff auf den OBD Desktop Agent und andere Tools zu erhalten.
              </p>
              <Button
                onClick={() => window.location.href = '/subscription'}
                style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
              >
                Abonnement upgraden
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Future Downloads Placeholder */}
        {hasOBDFeature && (
          <div className="mt-6 p-4 rounded-lg text-center" style={{ backgroundColor: '#2A3142' }}>
            <p className="text-sm" style={{ color: '#94A3B8' }}>
              Weitere Downloads und Tools werden hier in Zukunft verfügbar sein
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
